import { z } from 'zod';

/**
 * Generic port model (PRD §5.4): OLT ──PON── ODC ──output── ODP ──output── CPE.
 * A link always joins an upstream port (`pon`/`output`) to a downstream port (`input`).
 */
export const PORT_TYPES = ['pon', 'input', 'output'] as const;
export type PortType = (typeof PORT_TYPES)[number];

export const UPSTREAM_PORT_TYPES: readonly PortType[] = ['pon', 'output'];
export const isUpstreamPort = (t: string) => (UPSTREAM_PORT_TYPES as readonly string[]).includes(t);

/** Stored administrative state. `connected` is derived from `port_links`. */
export const PORT_STATUSES = ['available', 'reserved', 'maintenance'] as const;
export type PortStatus = (typeof PORT_STATUSES)[number];
export type PortState = PortStatus | 'connected';

export const PORT_STATE_LABELS: Record<PortState, string> = {
  available: 'Kosong',
  connected: 'Terpakai',
  reserved: 'Dipesan',
  maintenance: 'Maintenance',
};

export const PORT_TYPE_LABELS: Record<PortType, string> = {
  pon: 'Port PON',
  input: 'Port input',
  output: 'Port output',
};

const PORT_NAME_PREFIX: Record<PortType, string> = { pon: 'PON', input: 'IN', output: 'OUT' };
export const defaultPortName = (type: PortType, no: number) => `${PORT_NAME_PREFIX[type]} ${no}`;

/** Upstream asset type → downstream asset types it may feed. */
export const LINK_RULES: Record<string, readonly string[]> = {
  OLT: ['ODC', 'ODP'],
  ODC: ['ODC', 'ODP'],
  ODP: ['ODP', 'CPE'],
};

export function canLink(upstreamType: string, downstreamType: string): boolean {
  return LINK_RULES[upstreamType]?.includes(downstreamType) ?? false;
}

/** Asset types that may sit upstream of `downstreamType`. */
export function upstreamTypesFor(downstreamType: string): string[] {
  return Object.entries(LINK_RULES)
    .filter(([, down]) => down.includes(downstreamType))
    .map(([up]) => up);
}

const count = (v: unknown, fallback: number) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

/** How many ports of each type an asset should have, from its type and capacity fields (CON-02). */
export function portPlan(type: string, details: Record<string, unknown>): Partial<Record<PortType, number>> {
  switch (type) {
    case 'OLT':
      return { pon: count(details.ponPorts, 8) };
    case 'ODC':
      return { input: count(details.inputPorts, 1), output: count(details.outputPorts, 16) };
    case 'ODP':
      return { input: count(details.inputPorts, 1), output: count(details.outputPorts, 8) };
    case 'CPE':
      return { input: 1 };
    default:
      return {};
  }
}

export const hasPorts = (type: string) => Object.keys(portPlan(type, {})).length > 0;

export interface PortPeer {
  assetId: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  portId: string;
  portName: string;
  customerName: string | null;
  /** Peer is outside the viewer's areas: only its type is shown. */
  restricted: boolean;
}

export interface PortView {
  id: string;
  type: PortType;
  no: number;
  name: string;
  status: PortStatus;
  state: PortState;
  vlanId: number | null;
  notes: string | null;
  link: null | { id: string; lengthM: number | null; attenuationDb: number | null; peer: PortPeer };
}

export interface PortCandidate {
  assetId: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  customerName: string | null;
  distanceM: number;
  ports: Array<{ id: string; type: PortType; no: number; name: string; status: PortStatus }>;
}

export const portUpdateSchema = z.object({
  name: z.string().trim().min(1, 'Nama port wajib diisi').max(40).optional(),
  status: z.enum(PORT_STATUSES).optional(),
  vlanId: z.number().int().min(1, 'VLAN 1–4094').max(4094, 'VLAN 1–4094').nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type PortUpdateInput = z.infer<typeof portUpdateSchema>;

export const portLinkCreateSchema = z.object({
  portId: z.string().uuid(),
  peerPortId: z.string().uuid(),
  lengthM: z.number().min(0).max(100000).nullable().optional(),
  attenuationDb: z.number().min(0).max(60).nullable().optional(),
});
export type PortLinkCreateInput = z.infer<typeof portLinkCreateSchema>;

export interface TraceStep {
  assetId: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  lng: number;
  lat: number;
  restricted: boolean;
  /** Port on this asset that receives the signal (towards the OLT). */
  inPort: string | null;
  /** Port on the upstream asset that feeds `inPort`, with its PON VLAN. */
  upstreamPort: string | null;
  vlanId: number | null;
  linkLengthM: number | null;
  linkAttenuationDb: number | null;
  /** Splitter insertion loss inside this asset (ODC main splitter, ODP splitter). */
  splitterLossDb: number | null;
  splitterRatio: string | null;
}

export interface TraceResult {
  /** From the asset itself up to the OLT (or as far as links go). */
  steps: TraceStep[];
  /** True when the chain reaches an OLT PON port. */
  complete: boolean;
  /** Customers (CPE) fed through this asset. */
  downstreamCustomers: number;
  /** Rough optical loss OLT → this asset: splitters + measured links (or 0.35 dB/km). */
  estimatedLossDb: number | null;
}
